import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

type UseShareInviteOptions = {
    landingPath: string
    messageBuilder?: (shareInviteUrl: string) => string
    onCopy?: () => void
    onShare?: () => void
    fallbackBaseUrl?: string
    shareTitle?: string
    useEnvShareUrl?: boolean
    overrideShareOrigin?: string
    transformShareUrl?: (baseUrl: string) => string
}

type UseShareInviteResult = {
    shareInviteUrl: string
    shareInviteMessage: string
    whatsAppShareUrl: string
    isMobileShareAvailable: boolean
    isDesktopViewport: boolean
    hasCopiedLink: boolean
    handleCopyInvite: () => Promise<void>
    handleShareInvite: () => Promise<void>
}

const DEFAULT_FALLBACK_BASE_URL = 'https://app.contxt.network'
const DEFAULT_SHARE_TITLE = 'Kit invite'
const DEFAULT_USE_ENV_SHARE_URL = true

export function useShareInvite({
    landingPath,
    messageBuilder,
    onCopy,
    onShare,
    fallbackBaseUrl = DEFAULT_FALLBACK_BASE_URL,
    shareTitle = DEFAULT_SHARE_TITLE,
    useEnvShareUrl = DEFAULT_USE_ENV_SHARE_URL,
    overrideShareOrigin,
    transformShareUrl,
}: UseShareInviteOptions): UseShareInviteResult {
    const [isMobileShareAvailable, setIsMobileShareAvailable] = useState(false)
    const [isSmallScreen, setIsSmallScreen] = useState(() => {
        if (typeof window === 'undefined') return false
        return window.matchMedia('(max-width: 640px)').matches
    })
    const [hasCopiedLink, setHasCopiedLink] = useState(false)
    const copyResetTimerRef = useRef<number | null>(null)

    const baseShareInviteUrl = useMemo(() => {
        const envShareUrl = useEnvShareUrl
            ? (
                  import.meta.env.VITE_JOBSEEKER_SHARE_URL as string | undefined
              )?.trim()
            : undefined
        if (envShareUrl) {
            return envShareUrl
        }

        if (overrideShareOrigin?.trim()) {
            return `${overrideShareOrigin.trim().replace(/\/+$/, '')}${landingPath}`
        }

        if (typeof window !== 'undefined' && window.location?.origin) {
            return `${window.location.origin.replace(/\/+$/, '')}${landingPath}`
        }

        return `${fallbackBaseUrl}${landingPath}`
    }, [fallbackBaseUrl, landingPath, overrideShareOrigin, useEnvShareUrl])

    const shareInviteUrl = useMemo(() => {
        return typeof transformShareUrl === 'function'
            ? transformShareUrl(baseShareInviteUrl)
            : baseShareInviteUrl
    }, [baseShareInviteUrl, transformShareUrl])

    const shareInviteMessage = useMemo(() => {
        if (messageBuilder) {
            return messageBuilder(shareInviteUrl)
        }
        return `Check out this voice agent: ${shareInviteUrl}`
    }, [messageBuilder, shareInviteUrl])
    const normalizedShareMessage = useMemo(
        () => shareInviteMessage.replace(/\r\n/g, '\n'),
        [shareInviteMessage],
    )
    const whatsAppShareUrl = useMemo(
        () =>
            `https://wa.me/?text=${encodeURIComponent(normalizedShareMessage)}`,
        [normalizedShareMessage],
    )

    useEffect(() => {
        if (typeof window === 'undefined') return

        const mediaQuery = window.matchMedia('(max-width: 640px)')

        const evaluateShareSupport = () => {
            const hasNativeShare =
                typeof navigator !== 'undefined' &&
                typeof navigator.share === 'function'

            setIsSmallScreen(mediaQuery.matches)
            if (!hasNativeShare || !shareInviteUrl) {
                setIsMobileShareAvailable(false)
                return
            }

            const shareData: ShareData = {
                title: shareTitle,
                text: shareInviteMessage,
            }

            const canUseShare =
                !navigator.canShare || navigator.canShare(shareData)

            setIsMobileShareAvailable(mediaQuery.matches && canUseShare)
        }

        evaluateShareSupport()
        if (typeof mediaQuery.addEventListener === 'function') {
            mediaQuery.addEventListener('change', evaluateShareSupport)
        } else if (typeof mediaQuery.addListener === 'function') {
            mediaQuery.addListener(evaluateShareSupport)
        }

        return () => {
            if (typeof mediaQuery.removeEventListener === 'function') {
                mediaQuery.removeEventListener('change', evaluateShareSupport)
            } else if (typeof mediaQuery.removeListener === 'function') {
                mediaQuery.removeListener(evaluateShareSupport)
            }
        }
    }, [shareInviteMessage, shareInviteUrl, shareTitle])

    useEffect(() => {
        return () => {
            if (copyResetTimerRef.current) {
                window.clearTimeout(copyResetTimerRef.current)
            }
        }
    }, [])

    const copyTextToClipboard = useCallback(async (text: string) => {
        if (
            typeof navigator !== 'undefined' &&
            navigator.clipboard?.writeText
        ) {
            await navigator.clipboard.writeText(text)
            return
        }

        const textarea = document.createElement('textarea')
        textarea.value = text
        textarea.setAttribute('readonly', '')
        textarea.style.position = 'fixed'
        textarea.style.left = '-9999px'
        document.body.appendChild(textarea)
        textarea.select()
        const copied = document.execCommand('copy')
        document.body.removeChild(textarea)

        if (!copied) {
            throw new Error('Fallback copy failed')
        }
    }, [])

    const handleCopyInvite = useCallback(async () => {
        if (!shareInviteUrl) return

        if (onCopy) {
            onCopy()
        }

        try {
            await copyTextToClipboard(shareInviteUrl)
            setHasCopiedLink(true)
            if (copyResetTimerRef.current) {
                window.clearTimeout(copyResetTimerRef.current)
            }
            copyResetTimerRef.current = window.setTimeout(() => {
                setHasCopiedLink(false)
            }, 2000)
        } catch (copyError) {
            console.error('Failed to copy invite link', copyError)
            setHasCopiedLink(false)
        }
    }, [copyTextToClipboard, onCopy, shareInviteUrl])

    const handleShareInvite = useCallback(async () => {
        if (!isMobileShareAvailable) return

        if (onShare) {
            onShare()
        }

        try {
            await navigator.share({
                title: shareTitle,
                text: shareInviteMessage,
            })
        } catch (shareError) {
            if (
                shareError instanceof DOMException &&
                shareError.name === 'AbortError'
            ) {
                return
            }
            console.error('Failed to open native share sheet', shareError)
        }
    }, [isMobileShareAvailable, onShare, shareInviteMessage, shareTitle])

    return {
        shareInviteUrl,
        shareInviteMessage,
        whatsAppShareUrl,
        isMobileShareAvailable,
        isDesktopViewport: !isSmallScreen,
        hasCopiedLink,
        handleCopyInvite,
        handleShareInvite,
    }
}
